import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input, Modal, Switch, Table, message } from "antd";
import type { TableColumnsType } from "antd";
import { ChevronDown, ChevronUp, Pencil, Plus, Trash2 } from "lucide-react";

import OllieLayout from "components/layouts/ollie-layout/ollie-layout";
import LoadingOverlay from "components/loading-overlay/loading-overlay";

import { useGetPlanReviewSubmissionTypesAdminQuery } from "graphql/queries/get-plan-review-submission-types-admin.generated";
import { useInsertPlanReviewSubmissionTypeMutation } from "graphql/mutations/insert-plan-review-submission-type.generated";
import { useUpdatePlanReviewSubmissionTypeMutation } from "graphql/mutations/update-plan-review-submission-type.generated";
import { useDeletePlanReviewSubmissionTypeMutation } from "graphql/mutations/delete-plan-review-submission-type.generated";
import { useReorderPlanReviewSubmissionTypesMutation } from "graphql/mutations/reorder-plan-review-submission-types.generated";

import { usePlanReviewJurisdiction } from "hooks/usePlanReviewJurisdiction";
import { usePlanReviewSettingsAccess } from "hooks/usePlanReviewSettingsAccess";
import routeNames from "constants/route-names";
import { useDescribeScreenToOllie } from "state/reactive-vars/ollie-screen-description-var";
import { buildScreenDescription } from "utils/ollie-screen-description";

type SubmissionType = NonNullable<
	ReturnType<typeof useGetPlanReviewSubmissionTypesAdminQuery>["data"]
>["plan_review_submission_types"][number];

interface FormValues {
	id?: string;
	code: string;
	name: string;
	short_name: string;
	position: number;
	active: boolean;
}

const EMPTY: FormValues = { code: "", name: "", short_name: "", position: 0, active: true };

// The code is a machine identifier, so it is derived from the name rather than asked
// for: an AHJ manager naming a submittal type should not have to think about slugs.
const toCode = (name: string): string =>
	name
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "_")
		.replace(/^_+|_+$/g, "");

/**
 * The submittal types of one jurisdiction: what an applicant picks when starting a
 * plan review, and what each rule is scoped to.
 *
 * Positions are always 1..N with no gaps or ties. Moving a type to a position pushes
 * the others out of the way rather than letting two share a number, so the applicant's
 * dropdown has one unambiguous order.
 */
const PlanReviewSubmissionTypes = () => {
	const navigate = useNavigate();
	const { jurisdictionId, picker, emptyReason, isPlatformAdmin } = usePlanReviewJurisdiction();
	// Tells Ollie what the reader is looking at, and for which authority.
	useDescribeScreenToOllie(
		buildScreenDescription({
			screen:
				"The submission types screen: the kinds of submittal this authority accepts, which decide the rules a package is reviewed against. The reader maintains them here.",
			jurisdictionId
		})
	);

	const { canManage, blockChange, notice } = usePlanReviewSettingsAccess();
	// Appended to every control that writes: it reads as unavailable but still answers.
	const lockedClass = canManage ? "" : " prst-locked";

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [values, setValues] = useState<FormValues>(EMPTY);
	const [touched, setTouched] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const {
		data,
		loading,
		error: loadError,
		refetch
	} = useGetPlanReviewSubmissionTypesAdminQuery({
		skip: !jurisdictionId,
		variables: { jurisdictionId },
		fetchPolicy: "cache-and-network"
	});

	const [insertType] = useInsertPlanReviewSubmissionTypeMutation();
	const [updateType] = useUpdatePlanReviewSubmissionTypeMutation();
	const [deleteType] = useDeletePlanReviewSubmissionTypeMutation();
	const [reorderTypes] = useReorderPlanReviewSubmissionTypesMutation();

	const types = useMemo(() => data?.plan_review_submission_types ?? [], [data]);

	useEffect(() => {
		if (isModalOpen) setTouched(false);
	}, [isModalOpen]);

	// Renumbers the whole list 1..N from the order given, in one mutation.
	const persistOrder = useCallback(
		async (ordered: SubmissionType[]) => {
			const updates = ordered
				.map((type, index) => ({ type, position: index + 1 }))
				.filter(({ type, position }) => type.sort_order !== position)
				.map(({ type, position }) => ({
					where: { id: { _eq: type.id } },
					_set: { sort_order: position }
				}));
			if (updates.length > 0) await reorderTypes({ variables: { updates } });
		},
		[reorderTypes]
	);

	const move = useCallback(
		async (type: SubmissionType, direction: -1 | 1) => {
			if (!canManage) return blockChange();
			const current = types.findIndex(t => t.id === type.id);
			const target = current + direction;
			if (current < 0 || target < 0 || target >= types.length) return;
			const reordered = [...types];
			[reordered[current], reordered[target]] = [reordered[target], reordered[current]];
			try {
				await persistOrder(reordered);
				await refetch();
			} catch (moveError) {
				console.error("Submission type reorder failed:", moveError);
				message.error("We couldn't reorder the submission types. Please try again.");
			}
		},
		[types, persistOrder, refetch, canManage, blockChange]
	);

	const openRules = useCallback(
		(type: SubmissionType) => {
			const params = new URLSearchParams({ submissionType: type.id, jurisdiction: jurisdictionId });
			navigate(`${routeNames.PLAN_REVIEW_RULES}?${params.toString()}`);
		},
		[navigate, jurisdictionId]
	);

	const openCreate = useCallback(() => {
		if (!canManage) return blockChange();
		// A new type goes last unless the manager says otherwise.
		setValues({ ...EMPTY, position: types.length + 1 });
		setError(null);
		setIsModalOpen(true);
	}, [types.length, canManage, blockChange]);

	const openEdit = useCallback(
		(type: SubmissionType) => {
			if (!canManage) return blockChange();
			setValues({
				id: type.id,
				code: type.code,
				name: type.name,
				short_name: type.short_name ?? "",
				position: type.sort_order,
				active: type.active
			});
			setError(null);
			setIsModalOpen(true);
		},
		[canManage, blockChange]
	);

	// Two types with the same name are indistinguishable in the applicant's dropdown.
	const duplicateName = useMemo(() => {
		const name = values.name.trim().toLowerCase();
		if (!name) return false;
		return types.some(t => t.id !== values.id && t.name.trim().toLowerCase() === name);
	}, [types, values.id, values.name]);

	const handleSave = useCallback(async () => {
		setTouched(true);
		if (!values.name.trim() || duplicateName) return;
		setIsSaving(true);
		setError(null);
		try {
			const fields = {
				name: values.name.trim(),
				short_name: values.short_name.trim() || null,
				active: values.active
			};

			let savedId = values.id;
			if (values.id) {
				await updateType({ variables: { id: values.id, set: fields } });
			} else {
				const created = await insertType({
					variables: {
						object: {
							...fields,
							code: values.code.trim() || toCode(values.name),
							jurisdiction_id: jurisdictionId,
							// Parked at the end; the renumber below puts it where asked.
							sort_order: types.length + 1
						}
					}
				});
				savedId = created.data?.insert_plan_review_submission_types_one?.id;
			}

			// Rebuild the order with this type at the requested position, then renumber
			// everything 1..N so no two rows can end up sharing a number.
			const { data: fresh } = await refetch();
			const rows = [...(fresh?.plan_review_submission_types ?? [])];
			const moving = rows.findIndex(t => t.id === savedId);
			if (moving >= 0) {
				const [row] = rows.splice(moving, 1);
				const target = Math.min(Math.max(values.position, 1), rows.length + 1) - 1;
				rows.splice(target, 0, row);
				await persistOrder(rows);
				await refetch();
			}

			setIsModalOpen(false);
			message.success(values.id ? "Submission type updated." : "Submission type created.");
		} catch (saveError) {
			console.error("Submission type save failed:", saveError);
			const detail = saveError instanceof Error ? saveError.message : "";
			setError(
				detail.includes("name_uniq")
					? "Another submission type in your jurisdiction already uses that name."
					: detail.includes("uniq")
						? "Another submission type in your jurisdiction already uses that code."
						: `We couldn't save the submission type. ${detail}`.trim()
			);
		} finally {
			setIsSaving(false);
		}
	}, [values, duplicateName, updateType, insertType, jurisdictionId, types.length, refetch, persistOrder]);

	const handleDelete = useCallback(
		async (type: SubmissionType) => {
			if (!canManage) return blockChange();
			try {
				const result = await deleteType({ variables: { id: type.id } });
				// A row the permission filter excludes comes back as null with no error,
				// so success has to be read from the payload and not from "it didn't throw".
				if (!result.data?.delete_plan_review_submission_types_by_pk) {
					message.error("We couldn't delete that submission type.");
					return;
				}
				const { data: fresh } = await refetch();
				await persistOrder([...(fresh?.plan_review_submission_types ?? [])]);
				await refetch();
				message.success("Submission type deleted.");
			} catch (deleteError) {
				console.error("Submission type delete failed:", deleteError);
				const detail = deleteError instanceof Error ? deleteError.message : "";
				const ruleCount = type.plan_review_requirement_submission_types_aggregate.aggregate?.count ?? 0;
				message.error(
					!detail.toLowerCase().includes("foreign key")
						? "We couldn't delete the submission type. Please try again."
						: ruleCount > 0
							? "We couldn't delete that submission type because it has existing rules."
							: "This submission type is used by a plan review. Deactivate it instead."
				);
			}
		},
		[deleteType, refetch, persistOrder, canManage, blockChange]
	);

	const columns: TableColumnsType<SubmissionType> = useMemo(
		() => [
			{
				title: "Order",
				key: "sort_order",
				width: 96,
				render: (_, type, index) => (
					<div className="prst-order-cell">
						<span className="prst-order-number">{type.sort_order}</span>
						<div className="prst-order-buttons">
							<button
								type="button"
								className={`prst-icon-btn prst-icon-btn-tiny${lockedClass}`}
								title="Move up"
								aria-disabled={!canManage}
								disabled={index === 0}
								onClick={event => {
									event.stopPropagation();
									move(type, -1);
								}}
							>
								<ChevronUp size={13} />
							</button>
							<button
								type="button"
								className={`prst-icon-btn prst-icon-btn-tiny${lockedClass}`}
								title="Move down"
								aria-disabled={!canManage}
								disabled={index === types.length - 1}
								onClick={event => {
									event.stopPropagation();
									move(type, 1);
								}}
							>
								<ChevronDown size={13} />
							</button>
						</div>
					</div>
				)
			},
			{
				title: "Name",
				key: "name",
				render: (_, type) => (
					<div>
						<p className="prst-cell-title">{type.name}</p>
						<span className="prst-code">{type.code}</span>
					</div>
				)
			},
			{
				title: "Badge",
				dataIndex: "short_name",
				key: "short_name",
				width: 140,
				render: (shortName: string | null, type) => (
					<span className="prst-badge prst-badge-neutral">{shortName || type.name}</span>
				)
			},
			{
				title: "Rules",
				key: "rules",
				width: 90,
				render: (_, type) => type.plan_review_requirement_submission_types_aggregate.aggregate?.count ?? 0
			},
			{
				title: "",
				key: "actions",
				width: 100,
				render: (_, type) => (
					<div className="prst-cell-actions">
						<button
							type="button"
							className={`prst-icon-btn${lockedClass}`}
							title="Edit"
							aria-disabled={!canManage}
							onClick={event => {
								event.stopPropagation();
								openEdit(type);
							}}
						>
							<Pencil size={15} />
						</button>
						<button
							type="button"
							className={`prst-icon-btn${lockedClass}`}
							title="Delete"
							aria-disabled={!canManage}
							onClick={event => {
								event.stopPropagation();
								handleDelete(type);
							}}
						>
							<Trash2 size={15} />
						</button>
					</div>
				)
			}
		],
		[openEdit, handleDelete, move, types.length, canManage, lockedClass]
	);

	return (
		<>
			<OllieLayout>
				<div className="prst-page">
					<div className="prst-shell">
						<header className="prst-header">
							<div>
								<h1 className="prst-title">Submission Types</h1>
								<p className="prst-subtitle">
									What an applicant picks when starting a plan review with your jurisdiction, and what each rule is
									scoped to.
								</p>
							</div>
							<div className="prst-header-actions">
								<button
									className={`prst-btn prst-btn-primary${lockedClass}`}
									onClick={openCreate}
									aria-disabled={!canManage}
									disabled={!jurisdictionId}
								>
									<Plus size={16} />
									Add type
								</button>
							</div>
						</header>

						{isPlatformAdmin ? <div className="prst-header">{picker}</div> : null}

						{notice}

						{loadError ? (
							<div className="prst-empty">
								<p className="prst-empty-title">We couldn&apos;t load the submission types</p>
								<p>{loadError.message}</p>
							</div>
						) : emptyReason ? (
							<div className="prst-empty">
								<p className="prst-empty-title">{emptyReason.title}</p>
								<p>{emptyReason.detail}</p>
							</div>
						) : !loading && types.length === 0 ? (
							<div className="prst-empty">
								<p className="prst-empty-title">No submission types yet</p>
								<p>Add the kinds of submittal your office reviews, so applicants can pick one.</p>
							</div>
						) : (
							<Table
								className="prst-table"
								rowKey="id"
								columns={columns}
								dataSource={types}
								loading={loading && types.length === 0}
								pagination={false}
								rowClassName={type => `prst-row-clickable${type.active ? "" : " prst-row-inactive"}`}
								onRow={type => ({ onClick: () => openRules(type) })}
							/>
						)}
					</div>
				</div>
			</OllieLayout>

			<Modal
				open={isModalOpen}
				onCancel={() => setIsModalOpen(false)}
				footer={null}
				closable={false}
				width={560}
				className="prst-modal"
			>
				<h2 className="prst-modal-title">{values.id ? "Edit submission type" : "Add submission type"}</h2>
				<p className="prst-modal-sub">Only your jurisdiction sees and uses it.</p>

				<div className="prst-form">
					<div className="prst-form-field prst-form-field-full">
						<label className="prst-label">Name</label>
						<Input
							className="prst-control"
							placeholder="Name of the submittal, e.g. Fire Alarm System"
							value={values.name}
							onChange={e => setValues(v => ({ ...v, name: e.target.value }))}
							status={touched && (!values.name.trim() || duplicateName) ? "error" : undefined}
						/>
						{touched && duplicateName ? (
							<p className="prst-error">You already have a submission type with this name.</p>
						) : (
							<p className="prst-hint">
								The applicant will pick one submission type when starting a plan review within your jurisdiction.
							</p>
						)}
					</div>

					<div className="prst-form-field">
						<label className="prst-label">Short label (optional)</label>
						<Input
							className="prst-control"
							placeholder="e.g. Alarm"
							value={values.short_name}
							onChange={e => setValues(v => ({ ...v, short_name: e.target.value }))}
						/>
						<p className="prst-hint">One or two words for the badge on the dashboard. Defaults to the name.</p>
					</div>

					<div className="prst-form-field">
						<label className="prst-label">Position</label>
						<Input
							className="prst-control"
							type="number"
							min={1}
							max={values.id ? types.length : types.length + 1}
							value={values.position}
							onChange={value => setValues(v => ({ ...v, position: Number(value) || 1 }))}
						/>
						<p className="prst-hint">Where it sits in the applicant&apos;s dropdown. The others shift to make room.</p>
					</div>

					{values.id ? (
						<div className="prst-form-field prst-form-field-full">
							<label className="prst-label">Active?</label>
							<div>
								<Switch
									checked={values.active}
									onChange={checked => setValues(v => ({ ...v, active: checked }))}
									className="prst-switch"
								/>
								<span className="prst-hint">
									{" "}
									An inactive submission type will no longer be included as an option in the applicant&apos;s dropdown
									menu.
								</span>
							</div>
						</div>
					) : null}
				</div>

				{error ? <p className="prst-error">{error}</p> : null}

				<div className="prst-modal-actions">
					<button type="button" className="prst-btn" onClick={() => setIsModalOpen(false)} disabled={isSaving}>
						Cancel
					</button>
					<button type="button" className="prst-btn prst-btn-primary" onClick={handleSave} disabled={isSaving}>
						{isSaving ? "Saving…" : "Save type"}
					</button>
				</div>
			</Modal>

			<LoadingOverlay isLoading={isSaving} tip="Saving…" />
		</>
	);
};

export default PlanReviewSubmissionTypes;
